import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";

const API_BASE = "/api";

// ---------------------------------------------------------------------------
// Types — mirror backend response shapes
// ---------------------------------------------------------------------------
export type Usage = { prompt_tokens: number; completion_tokens: number; total_tokens: number };

export type Painpoint = {
  title: string;
  evidence: string;
  severity: number;
  recommended_products: string[];
};

export type ReportField = {
  value: string;
  details: string[];
  confidence: "high" | "medium" | "low" | string;
  sources: string[];
};

export type BusinessReport = {
  revenue: ReportField;
  recent_funding: ReportField;
  business_structure: ReportField;
  org_structure: ReportField;
};

export type CompanyBrief = {
  company_name: string;
  domain?: string | null;
  industry: string;
  size_hint?: string | null;
  tech_signals: Record<string, string[]>;
  summary: string;
  business_report?: BusinessReport;
  painpoints: Painpoint[];
  suggested_questions: string[];
  research_sources: { title: string; url: string }[];
};

export type CompanyResponse = {
  ok: boolean;
  request_id: string;
  session_id: string;
  elapsed_ms: number;
  brief: CompanyBrief;
  model: string;
  usage: Usage;
  sources: { web: string; site_fetched: boolean; industry_kb: string[] };
};

export type InfraPainpoint = {
  area: string;
  why: string;
  severity: number;
  datadog_products: string[];
  followup_questions: string[];
};

export type InfraResponse = {
  ok: boolean;
  request_id: string;
  session_id: string;
  elapsed_ms: number;
  stack_summary: string;
  painpoints: InfraPainpoint[];
  quick_wins: string[];
  matched_patterns: { id: string; title: string }[];
  candidate_products: { id: string; name: string }[];
  model: string;
  usage: Usage;
};

export type IntentDecoded = {
  surface_request: string;
  hidden_needs: string[];
  bant: Record<string, string>;
  risks: string[];
  followup_questions: string[];
  likely_objection: string;
};

export type IntentResponse = {
  ok: boolean;
  request_id: string;
  session_id: string;
  elapsed_ms: number;
  decoded: IntentDecoded;
  bant_signals: Record<string, string>;
  matched_objections: { id: string; title: string }[];
  model: string;
  usage: Usage;
};

export type DiscoveryQuestion = { q: string; category: string; why: string };

export type QuestionsResponse = {
  ok: boolean;
  request_id: string;
  session_id: string;
  elapsed_ms: number;
  stage: string;
  questions: DiscoveryQuestion[];
  model: string;
  usage: Usage;
};

export type POCAction = { title: string; owner: string; due: string; success_check: string };

export type POCPlan = {
  stage: string;
  next_actions: POCAction[];
  risks: string[];
  expansion_hooks: string[];
  exec_summary: string;
  _risk_score?: number;
};

export type POCResponse = {
  ok: boolean;
  request_id: string;
  session_id: string;
  elapsed_ms: number;
  stage: string;
  plan: POCPlan;
  blockers: string[];
  model: string;
  usage: Usage;
};

// ---------------------------------------------------------------------------
// transport
// ---------------------------------------------------------------------------
async function post<T>(
  path: string,
  body: Record<string, unknown>,
  action: string,
  extra: Record<string, unknown> = {},
): Promise<T> {
  const started = performance.now();
  const reqId = crypto.randomUUID();
  datadogRum.addAction(action, { ...extra, request_id: reqId });
  datadogLogs.logger.info(action, { ...extra, request_id: reqId });
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ request_id: reqId, ...body }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
    }
    const data = (await res.json()) as T;
    const elapsedMs = Math.round(performance.now() - started);
    datadogRum.addAction(`${action}.done`, { ...extra, request_id: reqId, elapsed_ms: elapsedMs });
    return data;
  } catch (err) {
    datadogRum.addError(err as Error, { action, request_id: reqId });
    datadogLogs.logger.error(`${action}.failed`, {
      ...extra,
      request_id: reqId,
      error: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}

export function researchCompany(args: {
  session_id: string;
  company_name: string;
  domain?: string;
  context?: string;
}): Promise<CompanyResponse> {
  return post<CompanyResponse>("/discovery/company", args, "discovery.company", {
    has_domain: !!args.domain,
  });
}

export function analyzeInfra(args: {
  session_id: string;
  company_name?: string;
  industry?: string;
  infra_text: string;
}): Promise<InfraResponse> {
  return post<InfraResponse>("/discovery/infra", args, "discovery.infra", {
    chars: args.infra_text.length,
  });
}

export function decodeIntent(args: {
  session_id: string;
  company_name?: string;
  quote: string;
}): Promise<IntentResponse> {
  return post<IntentResponse>("/discovery/intent", args, "discovery.intent", {
    chars: args.quote.length,
  });
}

export function generateQuestions(args: {
  session_id: string;
  company_name?: string;
  industry?: string;
  stage: string;
  focus?: string;
}): Promise<QuestionsResponse> {
  return post<QuestionsResponse>("/discovery/questions", args, "discovery.questions", {
    stage: args.stage,
  });
}

// ---------------------------------------------------------------------------
// v6 — Conversational FETCH discovery
// ---------------------------------------------------------------------------
// AE 5 (FETCH) + SE 5 (Architecture/Data/Risk/Provability/Stakeholders)
export type FetchSlot =
  | "F" | "E" | "T" | "C" | "H"
  | "A" | "D" | "R" | "P" | "S";

export type FollowupQuestion = {
  slot: FetchSlot;
  text: string;
  why: string;
  mom_test_compliant: boolean;
};

export type FetchState = {
  scores: Record<FetchSlot, number>;
  weakest: FetchSlot;
  notes: Record<FetchSlot, string>;
  is_complete: boolean;
  ae_avg: number;
  se_avg: number;
};

export type BriefAddendum = {
  slot: FetchSlot;
  heading: string;
  body: string;
  sources: string[];
};

export type BriefPatch = {
  addendum: BriefAddendum[];
  summary_delta: string;
};

export type ConversationTurn = {
  role: "user" | "agent";
  kind: string;
  payload: unknown;
};

export type ConverseResponse = {
  ok: boolean;
  request_id: string;
  session_id: string;
  elapsed_ms: number;
  turn_index: number;
  fetch_state: FetchState;
  next_questions: FollowupQuestion[];
  brief_patch?: BriefPatch | null;
  assistant_message: string;
  is_complete: boolean;
  brief?: CompanyBrief; // 첫 턴에만 포함
};

export function converseDiscovery(args: {
  session_id: string;
  company_name: string;
  domain?: string;
  context?: string;
  brief?: CompanyBrief | null;
  history: ConversationTurn[];
  max_questions?: number;
}): Promise<ConverseResponse> {
  return post<ConverseResponse>(
    "/discovery/converse",
    {
      session_id: args.session_id,
      company_name: args.company_name,
      domain: args.domain,
      context: args.context,
      brief: args.brief ?? null,
      history: args.history,
      max_questions: args.max_questions ?? 3,
    },
    "discovery.converse",
    {
      has_brief: !!args.brief,
      history_turns: args.history.length,
    },
  );
}

export function planPOC(args: {
  session_id: string;
  company_name?: string;
  current_stage?: string;
  status_text: string;
  success_metrics?: string[];
}): Promise<POCResponse> {
  return post<POCResponse>("/discovery/poc", args, "discovery.poc", {
    stage_hint: args.current_stage || "unknown",
  });
}
